import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Info, 
  AlertCircle, 
  ChevronRight, 
  DollarSign 
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { buildPatchFromChange } from '@/utils/patchMap';
import { useAuth } from '@/contexts/AuthContext';

// BLOKAS 1 AutoClose.tsx: INTERFACE AutoCloseProps
interface AutoCloseProps {
  botId?: string;
  timeBasedEnabled: boolean;
  onTimeBasedEnabledChange: (value: boolean) => void;
  closeAfterDurationEnabled: boolean;
  onCloseAfterDurationEnabledChange: (value: boolean) => void;
  closeAfterDuration: number;
  onCloseAfterDurationChange: (value: number) => void;
  closeAtSpecificTimeEnabled: boolean;
  onCloseAtSpecificTimeEnabledChange: (value: boolean) => void;
  closeAtTime: string;
  onCloseAtTimeChange: (value: string) => void;
  closeEndOfDayEnabled: boolean;
  onCloseEndOfDayEnabledChange: (value: boolean) => void;
  closeDayEnd: boolean;
  onCloseDayEndChange: (value: boolean) => void;
  closeOnFridayEveningEnabled: boolean;
  onCloseOnFridayEveningEnabledChange: (value: boolean) => void;
  closeFridayEnd: boolean;
  onCloseFridayEndChange: (value: boolean) => void;
  timezone: string;
  onTimezoneChange: (value: string) => void;
  globalProfitEnabled: boolean;
  onGlobalProfitEnabledChange: (value: boolean) => void;
  profitTarget: number;
  onProfitTargetChange: (value: number) => void;
  profitAmount: number;
  onProfitAmountChange: (value: number) => void;
  usePercentage: boolean;
  onUsePercentageChange: (value: boolean) => void;
  onUpdate?: (values: any) => void;
}

// BLOKAS 2 AutoClose.tsx: INTERFACE TooltipProps
interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
}

// BLOKAS 3 AutoClose.tsx: FUNKCIJA Tooltip
const Tooltip: React.FC<TooltipProps> = ({ content, children }) => (
  <div className="group relative inline-block">
    {children}
    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 w-96 hidden group-hover:block z-50">
      <div className="bg-gray-900 text-white text-sm rounded-lg p-4 shadow-xl">
        {content}
        <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-gray-900 transform rotate-45"></div>
      </div>
    </div>
  </div>
);

// BLOKAS 4 AutoClose.tsx: FUNKCIJA AutoClose
export const AutoClose: React.FC<AutoCloseProps> = ({ 
  botId,
  timeBasedEnabled,
  onTimeBasedEnabledChange,
  closeAfterDurationEnabled,
  onCloseAfterDurationEnabledChange,
  closeAfterDuration,
  onCloseAfterDurationChange,
  closeAtSpecificTimeEnabled,
  onCloseAtSpecificTimeEnabledChange,
  closeAtTime,
  onCloseAtTimeChange,
  closeEndOfDayEnabled,
  onCloseEndOfDayEnabledChange,
  closeDayEnd,
  onCloseDayEndChange,
  closeOnFridayEveningEnabled,
  onCloseOnFridayEveningEnabledChange,
  closeFridayEnd,
  onCloseFridayEndChange,
  timezone,
  onTimezoneChange,
  globalProfitEnabled,
  onGlobalProfitEnabledChange,
  profitTarget,
  onProfitTargetChange,
  profitAmount,
  onProfitAmountChange,
  usePercentage,
  onUsePercentageChange,
  onUpdate 
}) => {
  // SUB-BLOKAS 4.1 AutoClose.tsx: HOOK useAuth
  const { user } = useAuth();
  console.log(`🔧 [SUB-BLOKAS 4.1 AutoClose.tsx] useAuth hook initialized:`, { userId: user?.id });

  // BLOKAS 4.2 AutoClose.tsx: FUNKCIJA handleChange
  const handleChange = (field: string, value: any) => {
    console.log(`🔧 [BLOKAS 4.2 AutoClose.tsx] 📤 UNIFIED CHANGE HANDLER:`, {
      field,
      newValue: value,
      type: typeof value,
      botId: botId
    });
    
    // SUB-BLOKAS 4.2.1 AutoClose.tsx: SWITCH STATEMENT
    switch (field) {
      case 'timeBasedEnabled':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onTimeBasedEnabledChange:`, value);
        onTimeBasedEnabledChange(value);
        break;
      case 'closeAfterDurationEnabled':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseAfterDurationEnabledChange:`, value);
        onCloseAfterDurationEnabledChange(value);
        break;
      case 'closeAfterDuration':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseAfterDurationChange:`, value);
        onCloseAfterDurationChange(value);
        break;
      case 'closeAtSpecificTimeEnabled':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseAtSpecificTimeEnabledChange:`, value);
        onCloseAtSpecificTimeEnabledChange(value);
        break;
      case 'closeAtTime':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseAtTimeChange:`, value);
        onCloseAtTimeChange(value);
        break;
      case 'closeEndOfDayEnabled':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseEndOfDayEnabledChange:`, value);
        onCloseEndOfDayEnabledChange(value);
        break;
      case 'closeDayEnd':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseDayEndChange:`, value);
        onCloseDayEndChange(value);
        break;
      case 'closeOnFridayEveningEnabled':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseOnFridayEveningEnabledChange:`, value);
        onCloseOnFridayEveningEnabledChange(value);
        break;
      case 'closeFridayEnd':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onCloseFridayEndChange:`, value);
        onCloseFridayEndChange(value);
        break;
      case 'timezone':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onTimezoneChange:`, value);
        onTimezoneChange(value);
        break;
      case 'globalProfitEnabled':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onGlobalProfitEnabledChange:`, value);
        onGlobalProfitEnabledChange(value);
        break;
      case 'profitTarget':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onProfitTargetChange:`, value);
        onProfitTargetChange(value);
        break;
      case 'profitAmount':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onProfitAmountChange:`, value);
        onProfitAmountChange(value);
        break;
      case 'usePercentage':
        console.log(`🔧 [SUB-BLOKAS 4.2.1 AutoClose.tsx] Calling onUsePercentageChange:`, value);
        onUsePercentageChange(value);
        break;
    }
    
    // SUB-BLOKAS 4.2.2 AutoClose.tsx: onUpdate HANDLER
    if (onUpdate) {
      const completeState = {
        timeBasedEnabled: field === 'timeBasedEnabled' ? value : timeBasedEnabled,
        closeAfterDurationEnabled: field === 'closeAfterDurationEnabled' ? value : closeAfterDurationEnabled,
        closeAfterDuration: field === 'closeAfterDuration' ? value : closeAfterDuration,
        closeAtSpecificTimeEnabled: field === 'closeAtSpecificTimeEnabled' ? value : closeAtSpecificTimeEnabled,
        closeAtTime: field === 'closeAtTime' ? value : closeAtTime,
        closeEndOfDayEnabled: field === 'closeEndOfDayEnabled' ? value : closeEndOfDayEnabled,
        closeDayEnd: field === 'closeDayEnd' ? value : closeDayEnd,
        closeOnFridayEveningEnabled: field === 'closeOnFridayEveningEnabled' ? value : closeOnFridayEveningEnabled,
        closeFridayEnd: field === 'closeFridayEnd' ? value : closeFridayEnd,
        timezone: field === 'timezone' ? value : timezone,
        globalProfitEnabled: field === 'globalProfitEnabled' ? value : globalProfitEnabled,
        profitTarget: field === 'profitTarget' ? value : profitTarget,
        profitAmount: field === 'profitAmount' ? value : profitAmount,
        usePercentage: field === 'usePercentage' ? value : usePercentage
      };
      
      console.log(`🔧 [SUB-BLOKAS 4.2.2 AutoClose.tsx] 📤 CALLING onUpdate WITH COMPLETE STATE:`, completeState);
      onUpdate(completeState);
    }

    // SUB-BLOKAS 4.2.3 AutoClose.tsx: DATABASE SAVE LOGIC
    if (user?.id && botId) {
      console.log(`🔧 [SUB-BLOKAS 4.2.3 AutoClose.tsx] 💾 Saving to database immediately:`, {
        botId: botId,
        field,
        value
      });

      if (window.sendChange) {
        console.log(`🔧 [SUB-BLOKAS 4.2.3 AutoClose.tsx] 📤 Calling global sendChange function`);
        window.sendChange(field, value);
      }
    } else {
      console.log(`🔧 [SUB-BLOKAS 4.2.3 AutoClose.tsx] ⚠️ Cannot save to database - bot not available:`, {
        botId: botId
      });
    }
  };

  // BLOKAS 4.3 AutoClose.tsx: KINTAMASIS TimeBasedTooltip
  const TimeBasedTooltip = (
    console.log(`🔧 [BLOKAS 4.3 AutoClose.tsx] Initializing TimeBasedTooltip content`),
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full bg-blue-400"></div>
          <h5 className="font-medium text-blue-400">Time-based Auto-Close</h5>
        </div>
        <div className="space-y-3">
          <div>
            <p className="font-medium text-blue-400 mb-1">Features</p>
            <ul className="space-y-1 text-gray-300">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Close positions after specific duration
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Close at fixed time daily
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                End of day auto-closing
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Friday evening protection
              </li>
            </ul>
          </div>

          <div>
            <p className="font-medium text-blue-400 mb-1">Use Cases</p>
            <ul className="space-y-1 text-gray-300">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Day trading position management
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Weekend risk management
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Session-based trading
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-blue-300 text-xs">
          Configure time-based closures to protect your positions during off-hours and manage trading session boundaries effectively.
        </p>
      </div>
    </div>
  );

  // BLOKAS 4.4 AutoClose.tsx: KINTAMASIS GlobalProfitTooltip
  const GlobalProfitTooltip = (
    console.log(`🔧 [BLOKAS 4.4 AutoClose.tsx] Initializing GlobalProfitTooltip content`),
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full bg-green-400"></div>
          <h5 className="font-medium text-green-400">Global Profit Auto-Close</h5>
        </div>
        <div className="space-y-3">
          <div>
            <p className="font-medium text-green-400 mb-1">Features</p>
            <ul className="space-y-1 text-gray-300">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-green-400 flex-shrink-0" />
                Close all positions at profit target
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-green-400 flex-shrink-0" />
                Percentage or fixed amount targets
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-green-400 flex-shrink-0" />
                Portfolio-wide profit tracking
              </li>
            </ul>
          </div>

          <div>
            <p className="font-medium text-green-400 mb-1">Benefits</p>
            <ul className="space-y-1 text-gray-300">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-green-400 flex-shrink-0" />
                Lock in profits automatically
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-green-400 flex-shrink-0" />
                Protect winning positions
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-green-400 flex-shrink-0" />
                Meet daily/weekly profit goals
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2 bg-green-900/20 p-2 rounded">
        <AlertCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        <p className="text-green-300 text-xs">
          Set realistic profit targets based on your trading strategy and market conditions. Consider using percentage-based targets for better scaling with account size.
        </p>
      </div>
    </div>
  );

  // BLOKAS 4.5 AutoClose.tsx: JSX RETURN
  return (
    console.log(`🔧 [BLOKAS 4.5 AutoClose.tsx] Rendering AutoClose component JSX`),
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Auto-close</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure automatic position closing conditions</p>
          </div>
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <Clock className="h-6 w-6 text-blue-500" />
          </div>
        </div>

        <div className="space-y-8">
          {/* SUB-BLOKAS 4.5.1 AutoClose.tsx: TIME-BASED AUTO-CLOSE SECTION */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Time-based Auto-Close
                </h3>
                <Tooltip content={TimeBasedTooltip}>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <div className="flex items-center gap-3">
                <span className={cn(
                  "text-sm font-medium transition-colors",
                  timeBasedEnabled 
                    ? "text-blue-500 dark:text-blue-400" 
                    : "text-gray-400 dark:text-gray-500" 
                )}>
                  {timeBasedEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  onClick={() => handleChange('timeBasedEnabled', !timeBasedEnabled)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    timeBasedEnabled ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      timeBasedEnabled ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
            </div>

            {timeBasedEnabled && (
              <div className="space-y-6 animate-in fade-in duration-200">
                {/* SUB-BLOKAS 4.5.1.1 AutoClose.tsx: DURATION-BASED CLOSING */}
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Close after duration
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        "text-sm font-medium transition-colors",
                        closeAfterDurationEnabled 
                          ? "text-blue-500 dark:text-blue-400" 
                          : "text-gray-400 dark:text-gray-500" 
                      )}>
                        {closeAfterDurationEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleChange('closeAfterDurationEnabled', !closeAfterDurationEnabled)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                          closeAfterDurationEnabled ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            closeAfterDurationEnabled ? "translate-x-6" : "translate-x-1"
                          )}
                        />
                      </button>
                    </div>
                  </div>
                  {closeAfterDurationEnabled && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="1"
                          max="1440"
                          value={closeAfterDuration}
                          onChange={(e) => handleChange('closeAfterDuration', parseInt(e.target.value))}
                          className="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700"
                        />
                        <span className="w-20 text-right font-medium text-gray-900 dark:text-white">
                          {closeAfterDuration < 60 
                            ? `${closeAfterDuration}m` 
                            : `${Math.floor(closeAfterDuration/60)}h ${closeAfterDuration%60}m`}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Automatically close positions after this duration from entry time
                      </p>
                    </div>
                  )}
                </div>

                {/* SUB-BLOKAS 4.5.1.2 AutoClose.tsx: FIXED TIME CLOSING */}
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Close at specific time (UTC)
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        "text-sm font-medium transition-colors",
                        closeAtSpecificTimeEnabled 
                          ? "text-blue-500 dark:text-blue-400" 
                          : "text-gray-400 dark:text-gray-500" 
                      )}>
                        {closeAtSpecificTimeEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleChange('closeAtSpecificTimeEnabled', !closeAtSpecificTimeEnabled)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                          closeAtSpecificTimeEnabled ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            closeAtSpecificTimeEnabled ? "translate-x-6" : "translate-x-1"
                          )}
                        />
                      </button>
                    </div>
                  </div>
                  {closeAtSpecificTimeEnabled && (
                    <div className="space-y-4">
                      <input
                        type="time"
                        value={closeAtTime}
                        onChange={(e) => handleChange('closeAtTime', e.target.value)}
                        className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Close all positions at this specific UTC time daily
                      </p>
                    </div>
                  )}
                </div>

                {/* SUB-BLOKAS 4.5.1.3 AutoClose.tsx: END OF DAY CLOSING */}
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Close at end of day (23:55 UTC)
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        "text-sm font-medium transition-colors",
                        closeEndOfDayEnabled 
                          ? "text-blue-500 dark:text-blue-400" 
                          : "text-gray-400 dark:text-gray-500" 
                      )}>
                        {closeEndOfDayEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleChange('closeEndOfDayEnabled', !closeEndOfDayEnabled)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                          closeEndOfDayEnabled ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            closeEndOfDayEnabled ? "translate-x-6" : "translate-x-1"
                          )}
                        />
                      </button>
                    </div>
                  </div>
                  {closeEndOfDayEnabled && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Automatically close all positions at 23:55 UTC every day
                    </p>
                  )}
                </div>

                {/* SUB-BLOKAS 4.5.1.4 AutoClose.tsx: FRIDAY EVENING CLOSING */}
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Close on Friday evening (22:00 UTC)
                    </label>
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        "text-sm font-medium transition-colors",
                        closeOnFridayEveningEnabled 
                          ? "text-blue-500 dark:text-blue-400" 
                          : "text-gray-400 dark:text-gray-500" 
                      )}>
                        {closeOnFridayEveningEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleChange('closeOnFridayEveningEnabled', !closeOnFridayEveningEnabled)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                          closeOnFridayEveningEnabled ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            closeOnFridayEveningEnabled ? "translate-x-6" : "translate-x-1"
                          )}
                        />
                      </button>
                    </div>
                  </div>
                  {closeOnFridayEveningEnabled && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Weekend protection: Close all positions on Friday at 22:00 UTC
                    </p>
                  )}
                </div>

                {/* SUB-BLOKAS 4.5.1.5 AutoClose.tsx: TIMEZONE SELECTION */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => handleChange('timezone', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Europe/Vilnius">Europe/Vilnius</option>
                    <option value="Local">Local Time</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* SUB-BLOKAS 4.5.2 AutoClose.tsx: GLOBAL PROFIT AUTO-CLOSE SECTION */}
          <div className="space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Global Profit Auto-Close
                </h3>
                <Tooltip content={GlobalProfitTooltip}>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <div className="flex items-center gap-3">
                <span className={cn(
                  "text-sm font-medium transition-colors",
                  globalProfitEnabled 
                    ? "text-green-500 dark:text-green-400" 
                    : "text-gray-400 dark:text-gray-500" 
                )}>
                  {globalProfitEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  onClick={() => handleChange('globalProfitEnabled', !globalProfitEnabled)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    globalProfitEnabled ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      globalProfitEnabled ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
            </div>

            {globalProfitEnabled && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Use percentage target (instead of fixed amount)
                  </label>
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      "text-sm font-medium transition-colors",
                      usePercentage 
                        ? "text-green-500 dark:text-green-400" 
                        : "text-gray-400 dark:text-gray-500" 
                    )}>
                      {usePercentage ? 'Percentage' : 'Fixed Amount'}
                    </span>
                    <button
                      onClick={() => handleChange('usePercentage', !usePercentage)}
                      className={cn(
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                        usePercentage ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700"
                      )}
                    >
                      <span
                        className={cn(
                          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                          usePercentage ? "translate-x-6" : "translate-x-1"
                        )}
                      />
                    </button>
                  </div>
                </div>

                {usePercentage ? (
                  <div className="space-y-4 p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-sm font-medium text-green-700 dark:text-green-400">Percentage-based Profit Target</span>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Global profit target percentage
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="1"
                        max="100"
                        value={profitTarget}
                        onChange={(e) => handleChange('profitTarget', parseInt(e.target.value))}
                        className="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700"
                      />
                      <span className="w-16 text-right font-medium text-gray-900 dark:text-white">
                        {profitTarget}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Close all positions when total unrealized profit reaches {profitTarget}%
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Fixed Amount Profit Target</span>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Global profit target amount
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={profitAmount}
                        onChange={(e) => handleChange('profitAmount', parseInt(e.target.value))}
                        className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <DollarSign className="absolute left-2.5 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Close all positions when total unrealized profit reaches ${profitAmount}
                    </p>
                  </div>
                )}
                
                {/* SUB-BLOKAS 4.5.2.1 AutoClose.tsx: AUTO-CLOSE LOGIC SUMMARY */}
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border border-yellow-200 dark:border-yellow-800/30">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-400 mb-1">
                        Auto-Close Logic Summary
                      </p>
                      <div className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                        <p>• All time-based rules use UTC timezone</p>
                        <p>• Only enabled toggles will be active</p>
                        <p>• Global profit target: {usePercentage ? `${profitTarget}%` : `$${profitAmount}`}</p>
                        <p>• Positions will be closed automatically when conditions are met</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AutoClose;